import cn from 'clsx';
import Leaflet from 'leaflet';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Marker, Polyline, useMap } from 'react-leaflet';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { TO_MAP_CONSTS } from '@composite/map/container/constants';
import { MapTechnicalObjectsContext } from '@composite/map/context/map-technical-objects-context';
import { TechnicalObject } from '@models';
import { getToLabel, getVoltageLevel } from './oracle-types-mapper';
import { TO_MAP_COLORS } from './types';

const useStyles = makeStyles(() => ({
  '@keyframes blink': {
    '0%': {
      opacity: 0.2,
    },
    '50%': {
      opacity: 0.9,
    },
    '100%': {
      opacity: 0.2,
    },
  },
  polyline: {},
  selected: {
    filter: 'drop-shadow(0px 0px 10px black)',
  },
  hasDefect: {
    opacity: 0.6,
  },
  hidden: {
    opacity: 0.0,
  },
  hiddenSelected: {
    opacity: 0.8,
  },
  blinking: {
    animation: '$blink 1.0s linear infinite',
  },

  labelIcon: {
    fontSize: TO_MAP_CONSTS.labelsFontSize,
    whiteSpace: 'nowrap',
  },

  labelIconSelected: {
    fontSize: TO_MAP_CONSTS.labelsFontSizeSelected,
    textShadow: '1px 1px black',
    background: 'white',
    padding: '4px',
    width: 'fit-content',
    border: `3px solid black`,
  },
  labelIconSelectedWithDefect: {
    borderColor: TO_MAP_COLORS.DEFECT,
  },
  forceFront: {
    zIndex: `9999!important` as unknown as number,
  },
}));

const TechnicalObjectPolyline = ({
  techObject,
  isSelected,
  onClick,
}: {
  techObject: Partial<TechnicalObject>;
  isSelected?: boolean;
  onClick?: (
    e: L.LeafletMouseEvent,
    technicalObject: Partial<TechnicalObject>
  ) => void;
}) => {
  const voltageLevel = getVoltageLevel(techObject);

  const { showLabels } = useContext(MapTechnicalObjectsContext);
  const classes = useStyles();

  const map = useMap();
  const labelRef = useRef<L.Marker>(null);

  const positions = useMemo(
    () =>
      (techObject.coordinates ?? []).map(
        (c) => [c.latitude, c.longitude] as [number, number]
      ),
    [techObject.coordinates]
  );

  const color = TO_MAP_COLORS?.[voltageLevel] ?? TO_MAP_COLORS.TS;

  const markerIcon = useMemo(() => {
    return Leaflet.divIcon({
      className: `${cn('custom-div-icon', {
        [classes.forceFront]: isSelected,
      })}`,

      html: ReactDOMServer.renderToString(
        <div
          className={cn(classes.labelIcon, {
            [classes.labelIconSelected]: isSelected,
            [classes.labelIconSelectedWithDefect]: techObject.hasDefect,
          })}
          style={{ color }}
        >
          {getToLabel(techObject)}
        </div>
      ),
    });
  }, [classes, isSelected, techObject, color]);

  useEffect(() => {
    const updateLabel = () => {
      const zoom = map.getZoom();
      if (zoom > TO_MAP_CONSTS.labelsMinZoom && showLabels) {
        labelRef.current?.setOpacity(1);
      } else {
        labelRef.current?.setOpacity(0);
      }
    };
    map.on('zoomend', updateLabel);
    updateLabel();
    return () => {
      map.off('zoomend', updateLabel);
    };
  }, [map, showLabels]);

  if (positions.length === 0) return null;

  return (
    <>
      {/* Polyline below is duplicate polyline with much wider stroke, to help with selecting and also used for highlighting selected TO */}
      {isSelected && (
        <Polyline
          interactive={false}
          className={cn(classes.polyline, {
            [classes.selected]: isSelected,
            [classes.blinking]: true,
          })}
          weight={35}
          color={'white'}
          positions={positions}
        />
      )}

      {(techObject.hasDefect || techObject.visited) && (
        <Polyline
          interactive={false}
          className={classes.hasDefect}
          weight={20}
          color={
            techObject.visited ? TO_MAP_COLORS.VISITED : TO_MAP_COLORS.DEFECT
          }
          positions={positions}
        />
      )}

      <Polyline
        // na detekciu kliknutia, skryte
        eventHandlers={{
          click: (e) => onClick?.(e, techObject),
        }}
        className={cn(classes.polyline, {
          [classes.hidden]: true,
        })}
        weight={35}
        color={color}
        positions={positions}
      />
      <Polyline
        // klasika
        interactive={false}
        className={cn(classes.polyline, { [classes.selected]: isSelected })}
        weight={isSelected ? 6 : 3}
        color={color}
        positions={positions}
        dashArray={techObject.underground ? [0, 15, 0] : []}
      />

      <Marker
        ref={labelRef}
        eventHandlers={{ click: (e) => onClick?.(e, techObject) }}
        position={positions[0]}
        icon={markerIcon}
      />
    </>
  );
};

const arePropsEqual = (
  prevProps: Readonly<{
    techObject: Partial<TechnicalObject>;
    isSelected?: boolean;
    onClick?: (
      e: L.LeafletMouseEvent,
      technicalObject: Partial<TechnicalObject>
    ) => void;
  }>,
  nextProps: Readonly<{
    techObject: Partial<TechnicalObject>;
    isSelected?: boolean;
    onClick?: (
      e: L.LeafletMouseEvent,
      technicalObject: Partial<TechnicalObject>
    ) => void;
  }>
) => {
  return (
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.techObject === nextProps.techObject
  );
};

export default React.memo(TechnicalObjectPolyline, arePropsEqual);
